import { Controller, useForm } from "react-hook-form";
import styles from "./CalculatorForm.module.css";
import { NumericInput } from "../NumericInput";
import { useI18n } from "../../i18n";
import { TaxClassSelect } from "../TaxClassSelect";
import { FormField, FormFieldWithFieldset } from "../FormField";
import { type JSX, RefObject, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import debounce from "debounce";
import useDeepCompareEffect from "use-deep-compare-effect";
import {
  AppContext,
  AppDispatchContext,
  calculateWearPartsBudgetForServiceOption,
  CalculationActionTypes,
  ConfigurationActionTypes,
  getV1ServiceOptionByInspectionType,
  getV2IncludedInspectionPriceByBikeType,
} from "../../state";
import type {
  BikeTypeV2,
  FrameContractDTO,
  InputDataDTO,
  InspectionSubType,
  InspectionType,
  LeasingOptionType,
} from "@jobrad-gmbh/bike-leasing-calculator";
import { SimpleRadioGroup } from "../SimpleRadioGroup";
import { FormExpander } from "../FormExpander";
import { Details } from "../Details";
import { ServiceSelect } from "../ServiceSelect";
import { BikeTypeSelect } from "../BikeTypeSelect";
import { Hint } from "../Hint";
import { Checkbox } from "../Checkbox";
import { overridePrices } from "./overridePrices.ts";

interface CalculatorFormProps {
  scrollTargetRef?: RefObject<HTMLInputElement | null>;
}

// TODO review this type definition! Why do we need this after all!
export type InputDataUIModel = Override<
  Pick<
    InputDataDTO,
    | "fahrradtyp"
    | "kaufpreis"
    | "uvp"
    | "gehaltBrutto"
    | "steuerklasse"
    | "kirchensteuerPflichtig"
    | "krankenversicherung"
    | "serviceType"
    | "serviceSubType"
    | "arbeitgeberZuschuss"
    | "versicherungsZahler"
    | "vorsteuerabzugsberechtigt"
    | "serviceZahler"
    | "arbeitnehmerTyp"
  >,
  { arbeitgeberZuschuss: number | undefined }
>;

export function CalculatorForm(props: Readonly<CalculatorFormProps>): JSX.Element {
  const { t, formatNumber } = useI18n();
  const state = useContext(AppContext);
  const dispatch = useContext(AppDispatchContext);
  const scrollTargetRef = props.scrollTargetRef;

  const { elementVisibility } = state.configuration;
  const { defaultInputValues, bikeTypeOptions, bikeServiceOptions, userInput } = state.calculation;

  const { control, setValue, getValues, watch, handleSubmit } = useForm<InputDataUIModel>({
    defaultValues: {
      ...defaultInputValues,
      arbeitgeberZuschuss: undefined,
    },
    values: {
      ...defaultInputValues,
      ...userInput,
      arbeitgeberZuschuss:
        userInput?.arbeitgeberZuschuss && userInput?.arbeitgeberZuschuss > 0
          ? userInput?.arbeitgeberZuschuss
          : undefined,
    },
    mode: "all",
  });
  const kaufpreisRef = useRef<HTMLInputElement>(null);
  const uvpRef = useRef<HTMLInputElement>(null);
  const gehaltBruttoRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Focus the first element whenever calculator form gets expanded (expert mode enabled)
  useEffect(() => {
    if (state.configuration.isExpertMode) {
      const firstFocusableElement: HTMLElement | null | undefined = formRef?.current?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      firstFocusableElement?.focus();
    }
  }, [state.configuration.isExpertMode]);

  /*
    React Compiler ESLint rule suppression:
    Note: We are NOT using the React Compiler itself - this warning comes from the
    React Compiler ESLint rules that are included in eslint-plugin-react-hooks@7.0.0+.

    React Hook Form's watch() returns a proxy/function that React Compiler rules flag as
    incompatible with memoization. This is expected behavior - we use watchedData in
    useDeepCompareEffect (line 45) to trigger form submission on value changes.
    This is the recommended pattern from React Hook Form documentation.
  */
  // eslint-disable-next-line react-hooks/incompatible-library
  const watchedData = watch();
  const onValidSubmit = useCallback(
    (data: Partial<InputDataUIModel>) => {
      dispatch({ type: CalculationActionTypes.CALCULATE, payload: data });
      dispatch({ type: ConfigurationActionTypes.UPDATE_ELEMENT_VISIBILITIES, payload: { calculatorInput: data } });
    },
    [dispatch],
  );

  const onInvalidSubmit = useCallback(() => dispatch({ type: CalculationActionTypes.RESET_RESULTS }), [dispatch]);

  const debouncedSubmit = useMemo(
    () =>
      debounce(() => {
        handleSubmit(onValidSubmit, onInvalidSubmit)();
      }, 550),
    [handleSubmit, onValidSubmit, onInvalidSubmit],
  );

  const handleBikeTypeSelection = (newBikeType: BikeTypeV2, oldBikeType: BikeTypeV2): void => {
    const currentKaufpreis = getValues().kaufpreis;
    const currentUVP = getValues().uvp;
    const val = overridePrices(newBikeType, oldBikeType, currentKaufpreis, currentUVP);
    if (val.purchasePrice.override) {
      setValue("kaufpreis", val.purchasePrice.newValue);
    }
    if (val.uvp.override) {
      setValue("uvp", val.uvp.newValue);
    }
  };

  const handleExpertModeChange = (isExpertMode: boolean): void => {
    dispatch({ type: ConfigurationActionTypes.TOGGLE_EXPERT_MODE, payload: isExpertMode === true });
  };

  useDeepCompareEffect(() => {
    debouncedSubmit();
  }, [watchedData, state.calculation.calculator?.version]);

  const bikeServiceVersion = state.configuration.frameContract.bike_services?.services_version ?? "1.0";
  const isArbeitgeberzuschussVisible =
    elementVisibility.individualSubsidyInput &&
    !(
      (state.configuration.calculatorEnv === "website" || state.configuration.calculatorEnv === "dealer") &&
      !state.configuration.isExpertMode
    );

  const getArbeitnehmerTypSelect = ({ position }: { position: "top" | "bottom" }): JSX.Element | null => {
    if (
      state.configuration.elementVisibility.arbeitnehmerTypSelect &&
      ((position === "bottom" &&
        (state.configuration.calculatorEnv === "website" || state.configuration.calculatorEnv === "dealer") &&
        state.configuration.isExpertMode) ||
        (position === "top" && state.configuration.calculatorEnv === "agpss"))
    ) {
      return (
        <Controller
          control={control}
          name="arbeitnehmerTyp"
          render={({ field: { name, value } }) => (
            <FormFieldWithFieldset id="arbeitnehmerTypField" label={t("arbeitnehmerTypLabel")}>
              <SimpleRadioGroup
                options={[
                  {
                    name: t("details.arbeitnehmerTyp.angestellte"),
                    value: "angestellte",
                  },
                  {
                    name: t("details.arbeitnehmerTyp.beamte"),
                    value: "beamte",
                  },
                ]}
                variant="toggleButton"
                name="arbeitnehmerTyp"
                onChange={(event) => {
                  const value = event.target.value as "beamte" | "angestellte";
                  dispatch({ type: CalculationActionTypes.CHANGE_ARBEITNEHMER_TYP, payload: value });
                  // Trigger visibility update to toggle Krankenversicherung select
                  dispatch({
                    type: ConfigurationActionTypes.UPDATE_ELEMENT_VISIBILITIES,
                    payload: {
                      calculatorInput: { ...getValues(), arbeitnehmerTyp: value },
                    },
                  });
                  setValue(name, value);
                }}
                value={value}
                ariaLabelledBy="arbeitnehmerTypField_label"
              />
            </FormFieldWithFieldset>
          )}
        />
      );
    }

    return null;
  };

  return (
    <form className={styles.calculatorForm} ref={formRef}>
      {getArbeitnehmerTypSelect({ position: "top" })}
      <Controller
        control={control}
        name="fahrradtyp"
        render={({ field: { name, value } }) => (
          <FormFieldWithFieldset id="fahrradtypField" label={t("bikeTypeLabel")}>
            <BikeTypeSelect
              availableBikeTypes={bikeTypeOptions}
              selectedBikeType={value as BikeTypeV2}
              onValueChanged={(newValue: BikeTypeV2) => {
                handleBikeTypeSelection(newValue, value as BikeTypeV2);
                setTimeout(() => {
                  setValue(name, newValue);
                }, 1);
              }}
              ariaLabelledBy="fahrradtypField_label"
            ></BikeTypeSelect>
          </FormFieldWithFieldset>
        )}
      />
      <Controller
        control={control}
        name="kaufpreis"
        render={({ field: { name, value }, fieldState: { invalid, error } }) => (
          <FormField id="kaufpreisField" label={t("bikePriceLabel")} labelFor={name} errorMessage={error?.message}>
            <NumericInput
              id={name}
              name={name}
              value={value}
              hasError={invalid}
              onValueChanged={(newValue) => {
                setValue(name, newValue ?? 0, { shouldValidate: true, shouldTouch: true, shouldDirty: true });
                setValue("uvp", newValue ?? 0, { shouldDirty: false, shouldTouch: true });
              }}
              onChange={() => {
                ensureNiceCursorPosition(kaufpreisRef, getValues().kaufpreis, 0, formatNumber(getValues().kaufpreis));
              }}
              defaultValue={0}
              onBlur={() => {}}
              getInputRef={kaufpreisRef}
            />
            <Details
              details={t("details.bikePrice.details")}
              summary={t("details.bikePrice.summary")}
              id="details-bikePrice"
            />
          </FormField>
        )}
        rules={{
          required: t("errorMessages.bikePrice.required"),
          validate: {
            largerThanMin: (value: number) =>
              value >= state.configuration.frameContract.price_min ||
              t("errorMessages.bikePrice.largerThanMin", {
                minBikePrice: formatNumber(state.configuration.frameContract.price_min),
              }),

            lessThanMax: (value: number) =>
              value <= state.configuration.frameContract.price_max ||
              t("errorMessages.bikePrice.lessThanMax", {
                maxBikePrice: formatNumber(state.configuration.frameContract.price_max),
              }),
          },
        }}
      />
      {state.configuration.isExpertMode && (
        <Controller
          control={control}
          name="uvp"
          render={({ field: { name, value }, fieldState: { invalid, error } }) => (
            <FormField
              id="uvpField"
              label={t("uvpLabel")}
              labelFor={name}
              errorMessage={error?.message}
              isOptionalField
            >
              <NumericInput
                id={name}
                name={name}
                value={value}
                hasError={invalid}
                onValueChanged={(newValue) => {
                  setValue(name, newValue ?? getValues().kaufpreis, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  });
                }}
                onChange={() => {
                  ensureNiceCursorPosition(
                    uvpRef,
                    getValues().uvp,
                    getValues().kaufpreis,
                    formatNumber(getValues().uvp),
                  );
                }}
                onBlur={() => {}}
                defaultValue={getValues().kaufpreis}
                getInputRef={uvpRef}
                data-testid="input-uvp"
              />
              <Details details={t("details.uvp.details")} summary={t("details.uvp.summary")} id="details-uvp" />
            </FormField>
          )}
          rules={{
            validate: {
              largerThanOrEqualBikePrice: (value: number) => {
                return value >= getValues().kaufpreis || t("errorMessages.uvp.largerThanBikePrice");
              },
              // the validations for "largerThanMin" and "lessThanMax" only apply if "use_rrp_validation" is set to true
              largerThanMin: (value: number) =>
                state.configuration.frameContract.use_rrp_validation === false ||
                value >= state.configuration.frameContract.price_min ||
                t("errorMessages.uvp.largerThanMin", {
                  minBikePrice: formatNumber(state.configuration.frameContract.price_min),
                }),

              lessThanMax: (value: number) =>
                state.configuration.frameContract.use_rrp_validation === false ||
                value <= state.configuration.frameContract.price_max ||
                t("errorMessages.uvp.lessThanMax", {
                  maxBikePrice: formatNumber(state.configuration.frameContract.price_max),
                }),
            },
          }}
        />
      )}
      <Controller
        control={control}
        name="gehaltBrutto"
        render={({ field: { name, value }, fieldState: { invalid, error } }) => (
          <FormField id="gehaltBruttoField" label={t("salaryLabel")} labelFor={name} errorMessage={error?.message}>
            <NumericInput
              id={name}
              name={name}
              value={value}
              hasError={invalid}
              onValueChanged={(newValue) => {
                setValue(name, newValue ?? 0, { shouldValidate: true });
              }}
              onChange={() => {
                ensureNiceCursorPosition(
                  gehaltBruttoRef,
                  getValues().gehaltBrutto,
                  0,
                  formatNumber(getValues().gehaltBrutto),
                );
              }}
              onBlur={() => {}}
              defaultValue={0}
              getInputRef={gehaltBruttoRef}
            />
          </FormField>
        )}
        rules={{
          required: t("errorMessages.salary.required"),
          validate: {
            largerThanMin: (value: number) => {
              return value >= 197 || t("errorMessages.salary.largerThanMin");
            },
            lessThanMax: (value: number) => {
              return value < 100000 || t("errorMessages.salary.lessThanMax");
            },
          },
        }}
      />
      <Controller
        control={control}
        name="steuerklasse"
        render={({ field: { name, value } }) => (
          <FormFieldWithFieldset
            id="steuerklasseField"
            label={t("taxClassLabel")}
            tracking_id="JobRadRechner_Form_TaxSelector"
          >
            <TaxClassSelect
              id={name}
              name={name}
              value={value}
              onValueChanged={(newValue) => {
                setValue(name, newValue);
              }}
              ariaLabelledBy="steuerklasseField_label"
            />
          </FormFieldWithFieldset>
        )}
      />
      {state.configuration.isExpertMode && (
        <Controller
          control={control}
          name="kirchensteuerPflichtig"
          render={({ field: { name, value } }) => (
            <FormFieldWithFieldset id="kirchensteuerPflichtigField" label={t("churchTaxLabel")}>
              <SimpleRadioGroup
                options={[
                  {
                    name: t("details.churchTax.applicable"),
                    value: true,
                  },
                  {
                    name: t("details.churchTax.nonApplicable"),
                    value: false,
                  },
                ]}
                variant="toggleButton"
                name="kirchensteuerPflichtig"
                onValueChangedParse={(newValue) => {
                  setValue(name, newValue === "true");
                }}
                value={value}
                ariaLabelledBy="kirchensteuerPflichtigField_label"
              />
            </FormFieldWithFieldset>
          )}
        />
      )}
      {getArbeitnehmerTypSelect({ position: "bottom" })}
      {state.configuration.elementVisibility.krankenversicherungSelect && state.configuration.isExpertMode && (
        <Controller
          control={control}
          name="krankenversicherung"
          render={({ field: { onChange, value } }) => (
            <FormFieldWithFieldset id="krankenversicherungField" label={t("healthInsuranceLabel")}>
              <SimpleRadioGroup
                options={[
                  { name: t("healthInsuranceLabelGesetzlich"), value: "gesetzlich" },
                  { name: t("healthInsuranceLabelPrivat"), value: "privat" },
                ]}
                variant="toggleButton"
                name="krankenversicherung"
                onChange={onChange}
                value={value}
                ariaLabelledBy="krankenversicherungField_label"
              />
            </FormFieldWithFieldset>
          )}
        />
      )}
      {isArbeitgeberzuschussVisible && (
        <Controller
          control={control}
          name="arbeitgeberZuschuss"
          render={({ field: { name, value }, fieldState: { invalid, error } }) => (
            <FormField
              id="arbeitgeberZuschussField"
              label={t("subsidyLabel")}
              labelFor="arbeitgeberZuschuss"
              isOptionalField
              errorMessage={error?.message}
            >
              <NumericInput
                id={name}
                name={name}
                value={value}
                hasError={invalid}
                onValueChanged={(newValue) => {
                  setValue(name, newValue, { shouldValidate: true, shouldTouch: true, shouldDirty: true });
                }}
                getInputRef={scrollTargetRef}
              />
              <Details
                details={t("details.subsidy.details")}
                summary={t("details.subsidy.summary")}
                id="details-subsidy"
              />
            </FormField>
          )}
          rules={{
            validate: {
              lessThanBikePrice: (value: number | undefined) => {
                if (value === undefined) {
                  return true;
                }
                if (value < getValues().kaufpreis) {
                  return true;
                }
                return t("errorMessages.subsidy.largerThanBikePrice");
              },
            },
          }}
        />
      )}
      {
        /* we need to invert the values here: -> if checked ==> not vorsteuerabzugsberechtigt */
        elementVisibility.nvstSelect && state.configuration.isExpertMode && (
          <Controller
            control={control}
            name="vorsteuerabzugsberechtigt"
            render={({ field: { onChange, value, name } }) => (
              <FormField id="vorsteuerabzugsberechtigtField" labelFor="vorsteuerabzugsberechtigt">
                <Checkbox
                  isChecked={!value}
                  name={name}
                  onChange={(evt) => {
                    onChange(!evt.target.checked);
                  }}
                >
                  {t("vorsteuerabzugsberechtigtLabel")}
                </Checkbox>
                <Details
                  summary={t("details.vorsteuerabzugsberechtigt.summary")}
                  details={t("details.vorsteuerabzugsberechtigt.details")}
                  id="details-vorsteuerabzugsberechtigt"
                ></Details>
              </FormField>
            )}
          />
        )
      }
      {elementVisibility.serviceSelect && bikeServiceOptions.length > 0 && state.configuration.isExpertMode && (
        <Controller
          control={control}
          name="serviceType"
          render={({ field: { name } }) => (
            <FormFieldWithFieldset
              id="serviceTypeField"
              label={t(
                state.configuration.frameContract.bike_services?.is_mandatory
                  ? "servicePackageLabel"
                  : "servicePackageLabelOptional",
              )}
            >
              {bikeServiceVersion === "1.0" ? (
                <Details summary={t("details.servicePackage.v1.summary")} id="details-servicePackage-v1">
                  <>
                    {getV1ServiceOptionByInspectionType(bikeServiceOptions, "ivo") && (
                      <>
                        <b>{t("details.servicePackage.v1.details.inspection.name")}: </b>
                        <span>
                          {t("details.servicePackage.v1.details.inspection.description", {
                            value: getV1ServiceOptionByInspectionType(bikeServiceOptions, "ivo")?.default_bike
                              .inspection_price,
                          })}
                        </span>
                        <hr />
                      </>
                    )}
                    {getV1ServiceOptionByInspectionType(bikeServiceOptions, "full") && (
                      <>
                        <b>{t("details.servicePackage.v1.details.inspectionWearparts.name")}: </b>
                        <span>
                          {t("details.servicePackage.v1.details.inspectionWearparts.description", {
                            value: calculateWearPartsBudgetForServiceOption(
                              getV1ServiceOptionByInspectionType(bikeServiceOptions, "full"),
                            ),
                          })}
                        </span>
                      </>
                    )}
                  </>
                </Details>
              ) : (
                <Details summary={t("details.servicePackage.v2.summary")} id="details-servicePackage-v2">
                  {t("details.servicePackage.v2.details.included.1")}
                  <br />
                  {t("details.servicePackage.v2.details.included.2", {
                    value: getV2IncludedInspectionPriceByBikeType(
                      bikeServiceOptions,
                      getValues().fahrradtyp as BikeTypeV2,
                    ),
                  })}
                  <table>
                    <caption className={styles.caption}>
                      {t("details.servicePackage.v2.details.repairWear.label")}
                      <span>{t("details.servicePackage.v2.details.repairWear.hint")}</span>
                    </caption>
                    <tbody>
                      <tr>
                        <td className={styles.tableFirstColumn}>
                          {t("details.servicePackage.v2.details.repairWear.table.basis.label")}
                        </td>
                        <td className={styles.tableSecondColumn}>
                          {t("details.servicePackage.v2.details.repairWear.table.basis.value")}
                        </td>
                      </tr>
                      <tr>
                        <td>{t("details.servicePackage.v2.details.repairWear.table.komfort.label")}</td>
                        <td>
                          {t("details.servicePackage.v2.details.repairWear.table.komfort.value", {
                            value: bikeServiceOptions.find((service) => service.service_subtype === "komfort")?.[
                              getValues().fahrradtyp as BikeTypeV2
                            ].budget,
                          })}
                        </td>
                      </tr>
                      <tr>
                        <td>{t("details.servicePackage.v2.details.repairWear.table.unlimited.label")}</td>
                        <td>{t("details.servicePackage.v2.details.repairWear.table.unlimited.value")}</td>
                      </tr>
                    </tbody>
                  </table>
                  <p>
                    {t("details.servicePackage.v2.details.bikeDependentPrice")}
                    <br />
                    {t("details.servicePackage.v2.details.yourBikeSelection", {
                      bikeType: t(`bikeTypeOptions.${getValues().fahrradtyp}`),
                    })}
                  </p>
                </Details>
              )}

              <ServiceSelect
                services={bikeServiceOptions}
                inputValues={getValues()}
                frameContract={state.calculation.calculator?.frameContract as FrameContractDTO}
                onValueChanged={(selectedService: LeasingOptionType | undefined) => {
                  if (selectedService) {
                    setValue(name, selectedService.service_type as InspectionType, {
                      shouldValidate: true,
                      shouldTouch: true,
                      shouldDirty: true,
                    });
                    setValue("serviceSubType", selectedService.service_subtype as InspectionSubType, {
                      shouldDirty: false,
                      shouldTouch: true,
                    });
                  }
                }}
                ariaLabelledBy="serviceTypeField_label"
              ></ServiceSelect>

              {elementVisibility.serviceSelectHintEmployerPays && (
                <Hint variant="positive">{t("hints.employerPaysForYou")}</Hint>
              )}
            </FormFieldWithFieldset>
          )}
        />
      )}
      {elementVisibility.servicePayerSelect && state.configuration.isExpertMode && (
        <Controller
          control={control}
          name="serviceZahler"
          render={({ field: { onChange, value } }) => {
            return (
              <FormField id="serviceZahlerField" label={t("servicePayerLabel")} labelFor="serviceZahler">
                <SimpleRadioGroup
                  options={[
                    {
                      name: t("details.servicePackage.payer.company"),
                      value: "company",
                    },
                    {
                      name: t("details.servicePackage.payer.employee"),
                      value: "employee",
                    },
                  ]}
                  variant="toggleButton"
                  name="serviceZahler"
                  onChange={onChange}
                  value={value}
                  ariaLabelledBy="serviceZahlerField_label"
                />
              </FormField>
            );
          }}
        />
      )}
      {elementVisibility.insuranceSelect && state.configuration.isExpertMode && (
        <Controller
          control={control}
          name="versicherungsZahler"
          render={({ field: { onChange, value } }) => (
            <FormField id="versicherungsZahlerField" label={t("versicherungsZahlerLabel")}>
              <Details summary={t("details.versicherungsZahler.summary")} id="details-versicherungsZahler">
                <>
                  <div>{t("details.versicherungsZahler.details.0")}</div>
                  <div>{t("details.versicherungsZahler.details.1")}</div>
                </>
              </Details>
              <SimpleRadioGroup
                options={[
                  {
                    name: t("details.versicherungsZahler.company"),
                    value: "company",
                  },
                  {
                    name: t("details.versicherungsZahler.employee"),
                    value: "employee",
                  },
                ]}
                variant="toggleButton"
                name="versicherungsZahler"
                onChange={onChange}
                value={value}
                ariaLabelledBy="versicherungsZahlerField_label"
              />
            </FormField>
          )}
        />
      )}
      <FormExpander
        labelExpanded={t("lessDetails")}
        labelCollapsed={t("moreDetails")}
        isExpanded={state.configuration.isExpertMode === true}
        onToggle={handleExpertModeChange}
      />
    </form>
  );
}

const ensureNiceCursorPosition = (
  ref: RefObject<HTMLInputElement | null>,
  value: number,
  defaultValue: number,
  formattedNumber: string,
) => {
  const inputLength = formattedNumber.length;
  if (value !== defaultValue) {
    return;
  }
  setTimeout(() => {
    ref.current?.setSelectionRange(inputLength, inputLength);
  }, 200);
};
